import { ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import srcHeroPattern from './images/hero-pattern.svg';

interface HeroProps {
  nflAvailable?: boolean;
  cfbAvailable?: boolean;
}

const checklist = [
  {
    label: 'Proven record',
    description: (
      <>
        <span className="font-bold text-white">56%</span> ATS combined over the last three seasons,{' '}
        <span className="font-bold text-white">55%</span> over 29 years
      </>
    ),
  },
  {
    label: 'Proven edge',
    description: (
      <>
        Plays beat the closing line{' '}
        <span className="font-bold text-white">90%</span> of the time
      </>
    ),
  },
  {
    label: 'Records you can verify yourself',
    description: (
      <>
        Independently tracked at{' '}
        <a
          href="https://betstamp.app/u/RAS"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-white"
        >
          betstamp
        </a>{' '}
        since 2020
      </>
    ),
  },
  {
    label: 'Lines you can actually bet',
    description: 'Widely available numbers, not phantom openers',
  },
  {
    label: 'Sharp money backed',
    description: 'Run by and used by professional bettors',
  },
];

export default function Hero({ nflAvailable = false, cfbAvailable = false }: HeroProps) {
  const availableSports = [nflAvailable && 'NFL', cfbAvailable && 'CFB']
    .filter(Boolean)
    .join(' & ');

  return (
    <div className="relative isolate overflow-hidden">
      {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
      <div
        className="bg-repeat"
        style={{ backgroundImage: `url("${encodeURI(decodeURIComponent(srcHeroPattern))}")` }}
      >
        <div className="border-b border-zinc-600/80 bg-linear-to-bl from-emerald-400/50 to-70% px-6 sm:px-8 lg:px-16">
          <div className="mx-auto max-w-6xl pt-28 pb-24 lg:px-8 xl:px-16">

            {/* Two-column on desktop, stacked on mobile */}
            <div className="flex flex-col gap-12 lg:flex-row lg:items-center lg:gap-16">

              {/* Left: kicker + headline + lede + CTAs + fine print */}
              <div className="lg:flex-1">
                {/* Kicker — hidden on mobile */}
                <div className="mb-6 hidden items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 font-mono text-xs tracking-widest text-emerald-400 uppercase sm:inline-flex">
                  <span className="relative flex size-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex size-2 rounded-full bg-emerald-400" />
                  </span>
                  {availableSports ? `${availableSports} — Now Open` : 'NFL & College Football'}
                </div>

                {/* Headline */}
                <h1 className="font-bold text-4xl text-white leading-[0.98] tracking-tight sm:text-5xl lg:text-6xl">
                  Everything you're looking for in a{' '}
                  <span className="text-emerald-400">pick service.</span>
                </h1>

                {/* Lede */}
                <p className="mt-6 max-w-[46ch] text-base text-zinc-400 leading-relaxed sm:text-lg">
                  The right angle on every game. Long-term,{' '}
                  <span className="font-semibold text-white">third-party verified</span> edge — no
                  cherry-picked screenshots, no excuses.
                </p>

                {/* CTAs */}
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href="/order"
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 font-semibold text-sm text-white shadow-lg shadow-emerald-500/20 hover:bg-emerald-400 sm:text-base"
                  >
                    Start winning today <ArrowRightIcon className="size-4" />
                  </a>
                  <a
                    href="/results"
                    className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-6 py-3 font-semibold text-sm text-white hover:border-white/20 hover:bg-white/10 sm:text-base"
                  >
                    See the verified record
                  </a>
                </div>

                {/* Fine print — desktop only (mobile version lives below checklist) */}
                <p className="mt-5 hidden font-mono text-[11px] tracking-wide text-zinc-600 uppercase sm:block">
                  21+ · Please wager responsibly · Past results do not guarantee future returns
                </p>
              </div>

              {/* Right: checklist card */}
              <div className="w-full lg:w-[480px] lg:shrink-0">
                <div className="overflow-hidden rounded-lg border border-white/10 bg-white/[0.025]">
                  {checklist.map(({ label, description }, i) => (
                    <div
                      key={label}
                      className={`flex items-start gap-4 px-6 py-4 transition-colors hover:bg-emerald-400/[0.035] ${i < checklist.length - 1 ? 'border-b border-white/[0.06]' : ''}`}
                    >
                      <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border border-emerald-500/35 bg-emerald-500/15">
                        <CheckCircleIcon className="size-3.5 text-emerald-400" />
                      </span>
                      <span className="text-sm text-zinc-400 leading-snug sm:text-base">
                        <strong className="font-semibold text-white">{label}</strong>
                        {' — '}
                        {description}
                      </span>
                    </div>
                  ))}
                </div>
                {/* Fine print — mobile only */}
                <p className="mt-4 font-mono text-[11px] tracking-wide text-zinc-600 uppercase sm:hidden">
                  21+ · Please wager responsibly · Past results do not guarantee future returns
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
